import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
  ChevronDown20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { AvatarUploader } from "../../components/AvatarUploader";
import {
  ApiError,
  createSubcontractor,
  getSubcontractorSpecialties,
  uploadSubcontractorLogo,
  type Subcontractor,
  type SubcontractorPayload,
  type SubcontractorSpecialty,
} from "../../subempreiteiros/subempreiteirosApi";

type EstadoSubempreiteiro = "Ativo" | "Inativo";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar sub-empreiteiros.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível criar o sub-empreiteiro.";
};

const parseSpecialtiesError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar especialidades.";
    }
  }

  return "Não foi possível carregar as especialidades.";
};

export function NovoSubempreiteiroView() {
  const navigate = useNavigate();
  const [nome, setNome] = useState("");
  const [especialidadesDisponiveis, setEspecialidadesDisponiveis] =
    useState<SubcontractorSpecialty[]>([]);
  const [especialidadesSelecionadas, setEspecialidadesSelecionadas] =
    useState<number[]>([]);
  const [especialidadesErro, setEspecialidadesErro] = useState<
    string | null
  >(null);
  const [estado, setEstado] =
    useState<EstadoSubempreiteiro>("Ativo");
  const [pessoaContacto, setPessoaContacto] = useState("");
  const [nif, setNif] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [avatar, setAvatar] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let isMounted = true;

    const loadSpecialties = async () => {
      setEspecialidadesErro(null);
      try {
        const response = await getSubcontractorSpecialties();
        if (!isMounted) return;
        setEspecialidadesDisponiveis(response);
      } catch (error) {
        if (!isMounted) return;
        setEspecialidadesErro(parseSpecialtiesError(error));
      }
    };

    loadSpecialties();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleEspecialidade = (especialidadeId: number) => {
    if (especialidadesSelecionadas.includes(especialidadeId)) {
      setEspecialidadesSelecionadas(
        especialidadesSelecionadas.filter(
          (id) => id !== especialidadeId,
        ),
      );
    } else {
      setEspecialidadesSelecionadas([
        ...especialidadesSelecionadas,
        especialidadeId,
      ]);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!nome.trim()) {
      setSubmitError("O nome do sub-empreiteiro é obrigatório.");
      return;
    }

    if (especialidadesSelecionadas.length === 0) {
      setSubmitError(
        "A especialidade do sub-empreiteiro é obrigatória.",
      );
      return;
    }

    setIsSaving(true);

    const payload: SubcontractorPayload = {
      name: nome.trim(),
      taxId: nif.trim() || null,
      specialtyIds: especialidadesSelecionadas,
      status: estado,
      contactName: pessoaContacto.trim() || null,
      email: email.trim() || null,
      phoneNumber: telefone.trim() || null,
    };

    let savedSubcontractor: Subcontractor;

    try {
      savedSubcontractor = await createSubcontractor(payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    if (avatar) {
      try {
        await uploadSubcontractorLogo(
          savedSubcontractor.id,
          avatar,
        );
      } catch {
        setSubmitError(
          "Sub-empreiteiro criado, mas não foi possível guardar o logotipo.",
        );
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(false);
    navigate("/subempreiteiros", {
      state: { toast: "Sub-empreiteiro criado com sucesso" },
    });
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/subempreiteiros")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-subempreiteiro-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Sub-empreiteiro"}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Sub-empreiteiro"
        subtitle="Cria um novo sub-empreiteiro e define os dados de contacto."
        actions={actions}
        backButton={{ onClick: () => navigate("/subempreiteiros") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form
            id="novo-subempreiteiro-form"
            onSubmit={handleSubmit}
          >
            <div className="max-w-3xl mx-auto space-y-6 mb-20">
              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Informação Principal
                </h3>

                <div>
                  <label
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Logotipo do Sub-empreiteiro
                  </label>
                  <AvatarUploader
                    currentImage={null}
                    onImageChange={setAvatar}
                    variant="entity"
                    label=""
                    subtitle="Formatos suportados: JPG, PNG. Tamanho recomendado: 200×200."
                  />
                </div>

                <div>
                  <label
                    htmlFor="nomeSubempreiteiro"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Nome do Sub-empreiteiro{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <input
                    id="nomeSubempreiteiro"
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Insira o nome do sub-empreiteiro"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Especialidades{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {especialidadesDisponiveis.map(
                      (especialidade) => (
                        <button
                          key={especialidade.id}
                          type="button"
                          onClick={() =>
                            toggleEspecialidade(
                              especialidade.id,
                            )
                          }
                          className={`px-3 py-1.5 rounded transition-colors ${
                            especialidadesSelecionadas.includes(
                              especialidade.id,
                            )
                              ? "bg-primary text-primary-foreground"
                              : "bg-secondary hover:bg-accent border border-border text-foreground"
                          }`}
                          style={{
                            borderRadius: "var(--radius)",
                            fontSize: "var(--text-caption)",
                            fontWeight:
                              especialidadesSelecionadas.includes(
                                especialidade.id,
                              )
                                ? "var(--font-weight-semi-bold)"
                                : "var(--font-weight-normal)",
                          }}
                          disabled={isSaving}
                        >
                          {especialidade.name}
                        </button>
                      ),
                    )}
                  </div>
                  {especialidadesErro && (
                    <p className="mt-2 text-sm text-destructive">
                      {especialidadesErro}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="estado"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Estado
                  </label>
                  <div className="relative">
                    <select
                      id="estado"
                      value={estado}
                      onChange={(e) =>
                        setEstado(
                          e.target.value as EstadoSubempreiteiro,
                        )
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none pr-10"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isSaving}
                    >
                      <option value="Ativo">Ativo</option>
                      <option value="Inativo">Inativo</option>
                    </select>
                    <ChevronDown20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Contacto
                </h3>

                <div>
                  <label
                    htmlFor="pessoaContacto"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Pessoa de contacto
                  </label>
                  <input
                    id="pessoaContacto"
                    type="text"
                    value={pessoaContacto}
                    onChange={(e) =>
                      setPessoaContacto(e.target.value)
                    }
                    placeholder="Nome da pessoa de contacto"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="nif"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    NIF
                  </label>
                  <input
                    id="nif"
                    type="text"
                    value={nif}
                    onChange={(e) => setNif(e.target.value)}
                    placeholder="Número de Identificação Fiscal"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contacto@email.pt"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Telefone
                  </label>
                  <input
                    id="telefone"
                    type="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    placeholder="+351 912 345 678"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
